import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FormHelperText, Typography } from '@mui/material'
import { FormControl, MenuItem, Select } from '@mui/material'
import tw from 'twin.macro'
import { RiskMinor } from '@shopify/polaris-icons'

interface Props {
  name?: string
  css?: any
  label?: string
  placeholder?: string
  value?: string | number
  error?: boolean
  helperText?: string | false
  options: Array<BasicSelectOption>
  onChange(e: React.ChangeEvent<any>): void
  onBlur?: (e: React.FocusEvent<any>) => void
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  disabled?: boolean
}

const DEFAULT_SELECT_VALUE = 'DEFAULT_SELECT_VALUE'

export interface BasicSelectOption {
  label: string
  value: string | number
}

export const BasicSelect = ({
  name,
  css,
  label,
  placeholder,
  value,
  error,
  helperText,
  options,
  onChange,
  onBlur,
  inputProps,
  disabled,
}: Props) => {
  const formControlRef = useRef<HTMLDivElement>(null)
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (formControlRef.current) {
      setMenuWidth(formControlRef.current.clientWidth)
    }
  }, [])

  const handleOpen = () => {
    if (formControlRef.current) {
      setMenuWidth(formControlRef.current.clientWidth)
    }
  }

  const handleChange = (event: any) => {
    onChange(event)
  }

  const handleBlur = (event: any) => {
    if (onBlur) {
      onBlur(event)
    }
  }

  const _helperText = useMemo(() => {
    if (!helperText) {
      return null
    }

    if (error) {
      return (
        <FormHelperText css={tw`ml-0`} component="div">
          <div css={tw`flex items-center gap-1`}>
            <RiskMinor width={20} css={tw`fill-critical-500`} />
            <Typography variant="body2" css={tw`text-critical-500`} component="span">
              {helperText}
            </Typography>
          </div>
        </FormHelperText>
      )
    }

    return <FormHelperText>{helperText}</FormHelperText>
  }, [error, helperText])

  return (
    <div css={css}>
      <Typography css={tw`mb-1 text-shades-400`} variant="h6">
        {label}
      </Typography>
      <FormControl fullWidth error={error} disabled={disabled} ref={formControlRef}>
        <Select
          name={name}
          labelId="select-label"
          id="simple-select"
          inputProps={inputProps}
          disabled={disabled}
          value={value || DEFAULT_SELECT_VALUE}
          renderValue={(value) => {
            if (value === DEFAULT_SELECT_VALUE) {
              return <Typography color="rgba(0, 0, 0, 0.37)">{placeholder}</Typography>
            }

            return options.find((option) => option.value === value)?.label || value
          }}
          onOpen={handleOpen}
          onChange={handleChange}
          onBlur={handleBlur}
          MenuProps={{
            PaperProps: {
              style: {
                width: menuWidth,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} css={tw`whitespace-normal`}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {_helperText}
      </FormControl>
    </div>
  )
}
